import { RectangleData } from './rectangle-data.interface';
import { ImageAlbum } from './image-album.interface';
import { TextParams } from './text-params.interface';
import type { PropagatedBoxFields } from './propagated-box-fields';
import type {
  BezierPath,
  BoxTextWrap,
  CoverFit,
  VectorBoxParams,
  FioLayout,
  AdjustmentBoxParams,
  CustomTemplateBox,
  TextBoxParams,
  BoxEffects,
  BoxShadow,
  BoxStroke,
  BoxBevel,
  BoxGlow,
  BoxSatin,
  BoxPatternOverlay,
  BoxClippingState,
  ObjectAssetVersions,
  PsdLayerLockState,
  PsdLayerColorLabel,
  CompoundClipBakeRecipe,
  ImageFrameIntent,
  PsdGroupComposite,
  ImageFrameRole,
  ImageBoxParams,
  ObjectBoxParams,
  CustomImageTemplateBox,
} from '@lite-workspace/dto-fotoclass-templates';

/**
 * Композитные эффекты для drawable-объектов (тени, обводки, свечения и т.д.).
 * Применимы к любому типу drawable (image, text, object, background). Формы
 * shadow/stroke/glow совпадают с `PsShadowEffect`/`PsStrokeEffect`/`PsGlowEffect` из ps-effects —
 * эти же типы используются в canvas-engine `RenderBoxEffects`. Одна форма на
 * всё дерево рендеринга.
 *
 * `kind` — discriminator для union, остальные поля приходят из shared типа.
 */
export type DrawableEffect =
  | DrawableShadowEffect
  | DrawableStrokeEffect
  | DrawableGlowEffect
  | DrawableBevelEffect
  | DrawableSatinEffect
  | DrawablePatternEffect
  | (Readonly<NonNullable<BoxEffects['gradientOverlay']>> & { readonly kind: 'gradientOverlay' })
  | (Readonly<NonNullable<BoxEffects['colorOverlay']>> & { readonly kind: 'colorOverlay' });

export type DrawableShadowEffect = Readonly<BoxShadow> & { readonly kind: 'shadow' };
export type DrawableStrokeEffect = Readonly<BoxStroke> & { readonly kind: 'stroke' };
export type DrawableGlowEffect = Readonly<BoxGlow> & { readonly kind: 'glow' };
export type DrawableBevelEffect = Readonly<BoxBevel> & { readonly kind: 'bevel' };
export type DrawableSatinEffect = Readonly<BoxSatin> & { readonly kind: 'satin' };
export type DrawablePatternEffect = Readonly<BoxPatternOverlay> & { readonly kind: 'patternOverlay' };

/** Raw PSD box metadata, carried byte-for-byte between persist forms; the editor reads only a vector's v0 flip. */
export type BoxPassthroughMeta = {
  readonly psdLayerId?: string;
  readonly lock?: PsdLayerLockState;
  readonly colorLabel?: PsdLayerColorLabel;
  readonly clipping?: BoxClippingState;
  readonly groupId?: string;
  readonly name?: string;
  readonly flipHorizontal?: boolean;
  readonly flipVertical?: boolean;
  readonly rawEffects?: BoxEffects;
  /** ObjectBoxParams-only PSD-import workflow state (object/background boxes) — see passthrough.ts buildObjectPassthroughMeta. */
  readonly compoundClipBakeRecipe?: CompoundClipBakeRecipe;
  readonly imageFrameIntent?: ImageFrameIntent;
  /**
   * IMAGE-only box/params fields with no home on `DrawableImageData` — the design-import path writes them
   * and no editor code reads them, so they ride the bag verbatim instead of dying on every re-encode.
   * See passthrough.ts `buildImagePassthroughMeta`.
   */
  readonly imageBox?: {
    readonly decorSource?: ImageBoxParams['decorSource'];
    readonly color?: CustomImageTemplateBox['color'];
    readonly orientation?: CustomImageTemplateBox['orientation'];
    readonly fileTag?: ImageBoxParams['fileTag'];
    readonly sourceRef?: ImageBoxParams['sourceRef'];
    readonly versions?: ImageBoxParams['versions'];
    readonly frameStrokeBaked?: ImageBoxParams['frameStrokeBaked'];
    readonly cornerRadius?: ImageBoxParams['cornerRadius'];
    readonly rawEffects?: ImageBoxParams['rawEffects'];
  };
  /**
   * Запечённый стиль растрового декора (OBJECT): параметры эффектов, впечатанных в пиксели плитки, и поля
   * плитки вокруг слоя. Редактор их не рисует, «Рамка изображения» поднимает из них тень и размер слоя.
   * Отдельно от `rawEffects` выше: тот — снимок `box.effects` и сливается обратно в живые эффекты.
   * See passthrough.ts `buildObjectBakedStyleMeta`.
   */
  readonly objectBox?: {
    readonly rawEffects?: ObjectBoxParams['rawEffects'];
    readonly bakeInsets?: ObjectBoxParams['bakeInsets'];
  };
};

/**
 * `origin.fontName` snapshots the layer's fontName at build time — normalizer must drop this bag
 * (not carry it forward) once `drawable.text.fontName` diverges from it, or stale typography
 * silently survives a font change.
 */
export type TextPassthroughMeta = BoxPassthroughMeta &
  Pick<
    TextBoxParams,
    | 'superscriptSize'
    | 'superscriptPosition'
    | 'subscriptSize'
    | 'subscriptPosition'
    | 'smallCapSize'
    | 'useFractionalGlyphWidths'
    | 'frameOnPenBox'
    | 'antiAlias'
    | 'firstBaselineOffset'
    | 'firstBaselineRule'
    | 'firstBaselineMin'
    | 'textLayout'
    | 'firstLineIndent'
    | 'startIndent'
    | 'endIndent'
    | 'spaceBefore'
    | 'spaceAfter'
    | 'autoHyphenate'
    | 'hyphenatedWordSize'
    | 'preHyphen'
    | 'postHyphen'
    | 'consecutiveHyphens'
    | 'hyphenationZone'
    | 'language'
    | 'wordSpacing'
    | 'paragraphLetterSpacing'
    | 'glyphSpacing'
    | 'leadingType'
    | 'everyLineComposer'
    | 'hanging'
    | 'styleRuns'
    | 'paragraphRuns'
    | 'fontSizePt'
    | 'lineHeightPt'
    | 'leading'
  > & {
    readonly origin: { readonly fontName: string };
  };

/** SmartObject is a hybrid de-escalation, not a union variant — see custom-template.ts BoxType.SMART_OBJECT. */
export type SmartObjectPassthroughMeta = {
  readonly boxType: 'object' | 'smartObject';
  readonly sourceType?: string;
  readonly sourceId?: string;
  readonly rawEffects?: BoxEffects;
  readonly rawTransform?: readonly number[];
  readonly snapshotRect?: RectangleData;
};

interface DrawableBase extends PropagatedBoxFields {
  readonly id: string;
  readonly idFromTemplate?: string;
  readonly order: number;
  readonly isTemplateObject?: boolean;
  readonly isNullImage?: boolean;
  readonly skipRender?: boolean;
  /**
   * Parse-time classification mirrored from `CustomTemplateBoxBase.backgroundExcluded` — the box reads as
   * content, not as part of the spread's background. The editor never writes it; it rides on the drawable
   * (not in `templateMeta`) so a fontName change, which drops the whole passthrough bag, cannot take it.
   */
  readonly backgroundExcluded?: boolean;
  /** Решение закона пересчёта обложки под корешок (`CustomTemplateBoxBase.coverFit`) — кодек несёт как есть. */
  readonly coverFit?: CoverFit;
  /**
   * Слой пришёл с плитки фона (`CustomTemplateBoxBase.wornFromBackground`). Альбом метку не ставит — он несёт её
   * до «Сохранить как шаблон», чтобы смена фона в паке сняла этот декор вместе со старым фоном. Читают её, среди
   * прочих, рендер-адаптер (`TemplateBoxAdapter`: слой надетого фона лежит под текстом разворота и обтекания не даёт)
   * и `findEmptySpreads` (слой надетого фона — часть фона, а не содержимое разворота).
   */
  readonly wornFromBackground?: boolean;
  /** Слой разворота (`CustomTemplateBoxBase.spreadWide`, ADR 0045): голова коллажа 0, страницы у него нет. */
  readonly spreadWide?: boolean;
  /** InDesign Text Wrap (`CustomTemplateBoxBase.textWrap`): the page's texts set around this box; carried as is. */
  readonly textWrap?: BoxTextWrap;
  readonly objectKey?: string;
  readonly canResize: boolean;
  readonly effects?: readonly DrawableEffect[];
  readonly templateMeta?: BoxPassthroughMeta;
}

export interface DrawableImageData extends DrawableBase {
  readonly type: 'image';
  /** Роль рамки (S12): принимаемый род кадра; отсутствие — рамка судится по роду своей группы. */
  readonly frameRole?: ImageFrameRole;
  readonly image: ImageAlbum | null;
  /** Кадр, погашенный спором «это не я»: рамка пуста, но помнит его для возврата по «да, это он». */
  readonly disputedPhoto?: ImageAlbum;
  readonly templateImage?: ImageAlbum;
  readonly hasFaceOnFold: boolean;
  readonly faceOnFoldAccepted: boolean;
  /** Photo fit inside the frame; absent = cover-fill (scale to cover, crop). `contain` keeps the whole photo visible. */
  readonly fit?: 'contain';
  /** Зеркалирование фото внутри рамки (применяется к image, не к container). */
  readonly flipHorizontal?: boolean;
  readonly flipVertical?: boolean;
  readonly naturalAligns: {
    readonly container: RectangleData;
    readonly image: RectangleData;
  };
  /**
   * Base64 PNG alpha mask inherited from the template's clipping-group base layer.
   * Applied by the renderer on top of whichever image is currently in the slot
   * (preview or user-dropped) so the photo takes the decorative silhouette shape.
   */
  readonly maskDataUrl?: string;
  readonly maskRect?: ImageBoxParams['maskRect'];
  /** Uploaded alpha-clipping mask (alpha-only PNG in storage); preferred over `maskDataUrl`. */
  readonly maskUrl?: string;
  readonly maskFileId?: string;
  /**
   * Id of the layer this frame is clipped to (Photoshop's clipping mask) — the mask's pixels lie on that
   * layer's rect, so the frame slides under a standing mask instead of carrying it along.
   */
  readonly maskBaseBoxId?: string;
  /**
   * Vector clipping paths inherited from the template's photo-frame vectorMask.
   * Applied by the renderer as a Konva `clipFunc` so the photo + preview + overlays
   * keep the rounded silhouette of a non-rectangular PSD photo frame.
   * Coordinates are in container-local space (relative to naturalAligns.container top-left).
   */
  readonly paths?: readonly BezierPath[];
}

export interface DrawableTextData extends DrawableBase {
  readonly type: 'text';
  readonly text: TextParams;
  readonly textType?: string;
  readonly fioLayout?: FioLayout;
  readonly textMode?: 'block' | 'free';
  readonly autoWidth?: boolean;
  readonly autoFit?: boolean;
  readonly maxFontSize?: number;
  readonly verticalAlign?: 'top' | 'center' | 'bottom';
  readonly naturalAligns: {
    readonly container: RectangleData;
    readonly fontSize: number;
  };
  /** Only `text` persists (`TextBoxParams.templateText`); a decoded v1 document comes back without `naturalAligns`. */
  readonly templateParams?: {
    readonly text: string;
    readonly naturalAligns?: {
      readonly container: RectangleData;
      readonly fontSize: number;
    };
  };
  readonly templateMeta?: TextPassthroughMeta;
  /** Provenance: the CLIENT (student) typed this value, so a rebuild keeps it instead of the master's word. */
  readonly editedByClient?: boolean;
  /** Which entry of the album's cover text memory this slot prints — stamped by the carry, so an edit goes back to ITS place. */
  readonly memoryEntry?: number;
  /** Lead quote line under the teachers' band (R427): fill prints «quote — First Patronymic»; absent — an ordinary LEAD_QUOTE. */
  readonly leadLine?: boolean;
  /** Quote frame of the vignette series (R409): the fill picks the print size from the capacities below. */
  readonly vignetteQuote?: true;
  /** Frame capacity in characters at the nominal font size. */
  readonly quoteNominalCapacity?: number;
  /** Frame capacity at the floor font size — the quote length limit (R430). */
  readonly quoteFloorCapacity?: number;
  /** Floor font size of the quote frame (R430), the one `quoteFloorCapacity` was measured at. */
  readonly quoteFloorFontSize?: number;
}

export interface GroupBoxParams {
  readonly personId: string | null;
  readonly peopleType: string;
  /**
   * Род кадра, реально вставшего в рамку по лестнице фоллбека генерации, когда собственных кадров
   * структурной роли (`peopleType`) не хватило: рамка принимает род вставшего кадра. Пишет генерация,
   * читают панель фото и правила перетаскивания; поля нет — рамка держит свой структурный род.
   */
  readonly adoptedPeopleType?: string;
  /** PSD group composite, carried as is by the group codec — see the DTO `GroupBoxParams.psdGroup`. */
  readonly psdGroup?: PsdGroupComposite;
  /** Крупная карточка владельца в серии карточек с цитатами (R429); поля нет — обычная ячейка. */
  readonly ownerCard?: boolean;
  /** Карточка владельца из ячейки пака «Владелец альбома», а не карточка сборки Люмир. */
  readonly packOwnerCard?: boolean;
}

export interface DrawableGroupData extends DrawableBase {
  readonly type: 'group';
  readonly params: GroupBoxParams;
  readonly drawableObjects: readonly DrawableData[];
  readonly draggable?: boolean;
  readonly naturalAligns: {
    readonly container: RectangleData;
  };
}

export interface DrawableBackgroundData extends DrawableBase {
  readonly type: 'background';
  /** When true, background was explicitly applied by user (not part of cover/template design) */
  readonly userApplied?: boolean;
  /** Pack-origin marker: the background came from a design pack, not from the collage's own layout template. */
  readonly packApplied?: boolean;
  /** The pack spread this background was taken off — album load re-dresses from it when the pack's look moved on. */
  readonly sourceSpreadId?: string;
  readonly sourcePackId?: string;
  /**
   * The container is where a person put it, not where the layout left it. The full-bleed normalizer treats a
   * page-sized container off the page as a lost bleed and snaps it back — that must not erase a deliberate move.
   */
  readonly placed?: boolean;
  readonly data: {
    readonly url: string;
    readonly fileId: string;
    readonly versions?: ObjectAssetVersions;
  };
  readonly maskRect?: ImageBoxParams['maskRect'];
  readonly maskUrl?: string;
  readonly maskFileId?: string;
  readonly naturalAligns: {
    readonly container: RectangleData;
    readonly content?: RectangleData;
  };
}

export interface DrawableObjectData extends DrawableBase {
  readonly type: 'object';
  readonly data?: {
    readonly url?: string;
    readonly fileId?: string;
    readonly versions?: ObjectAssetVersions;
  };
  readonly maskDataUrl?: string;
  readonly maskRect?: ImageBoxParams['maskRect'];
  readonly maskUrl?: string;
  readonly maskFileId?: string;
  /** Id of the layer this decor is clipped to — a clipping group's photo slot the designer left as decor. */
  readonly maskBaseBoxId?: string;
  readonly pixelData?: string;
  /** Hybrid de-escalation for BoxType.SMART_OBJECT — see SmartObjectPassthroughMeta doc-comment. */
  readonly objectMeta?: SmartObjectPassthroughMeta;
  readonly naturalAligns: {
    readonly container: RectangleData;
    readonly content?: RectangleData;
  };
}

export interface DrawableVectorData extends DrawableBase {
  readonly type: 'vector';
  readonly vector: VectorBoxParams;
  /** Зеркалирование фигуры слоя (флип бокса шаблона); отсутствует — не отражена. */
  readonly flipHorizontal?: boolean;
  readonly flipVertical?: boolean;
  readonly naturalAligns: { readonly container: RectangleData; readonly content?: RectangleData };
}

export interface DrawableAdjustmentData extends DrawableBase {
  readonly type: 'adjustment';
  readonly params: AdjustmentBoxParams;
  readonly naturalAligns: { readonly container: RectangleData };
}

/** Backstop for any BoxType not covered by the union above — prevents a new PSD box type from silently vanishing. */
export interface DrawableUnsupportedData extends DrawableBase {
  readonly type: 'unsupported';
  readonly sourceBoxType: string;
  readonly raw?: CustomTemplateBox;
  readonly naturalAligns: { readonly container: RectangleData };
}

/** Discriminated union — используй drawable.type для narrowing */
export type DrawableData =
  | DrawableImageData
  | DrawableTextData
  | DrawableGroupData
  | DrawableBackgroundData
  | DrawableObjectData
  | DrawableVectorData
  | DrawableAdjustmentData
  | DrawableUnsupportedData;

/** Type guard functions */
export function isDrawableImage(d: DrawableData): d is DrawableImageData {
  return d.type === 'image';
}

export function isDrawableText(d: DrawableData): d is DrawableTextData {
  return d.type === 'text';
}

export function isDrawableGroup(d: DrawableData): d is DrawableGroupData {
  return d.type === 'group';
}

export function isDrawableBackground(d: DrawableData): d is DrawableBackgroundData {
  return d.type === 'background';
}

export function isDrawableVector(d: DrawableData): d is DrawableVectorData {
  return d.type === 'vector';
}

export function isDrawableAdjustment(d: DrawableData): d is DrawableAdjustmentData {
  return d.type === 'adjustment';
}

export function isDrawableUnsupported(d: DrawableData): d is DrawableUnsupportedData {
  return d.type === 'unsupported';
}
